/**
 * Label lookups for engine-supplied keys.
 *
 * The strings themselves live in the `common` translation catalogue; this module
 * only holds the key mapping and the fallbacks. Module-scope `t()` is impossible
 * (i18next isn't initialised at import time and the value wouldn't re-render on a
 * language change), so every lookup takes a `t` from the calling component.
 *
 * Every lookup degrades gracefully: these keys come from the Python engine, so a
 * phase we don't know about must render as readable text, not a raw key.
 */
/**
 * Minimal shape of i18next's `t`. Typing the parameter structurally (rather than as
 * i18next's generic TFunction) means components can pass their namespaced `t`
 * straight in — no casts at the call site.
 */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Humanise an unknown engine key: `crew_plan` → `crew plan`. */
const humanise = (key: string) => key.replace(/_/g, ' ').trim();

/** Pipeline phase → what the crew is doing, in production vernacular. */
export const crewPhaseLabel = (t: Translate, phase?: string): string =>
  phase ? t(`phase.${phase}`, { defaultValue: humanise(phase) }) : '';

/** Pipeline phase → the formal stage name (used on the 4-step stage tracker). */
export const stageLabel = (t: Translate, phase?: string): string =>
  phase ? t(`stage.${phase}`, { defaultValue: humanise(phase) }) : '';

/** Open review-gate phase → what the producer is being asked to approve. */
export const gateLabel = (t: Translate, phase?: string): string =>
  phase ? t(`gate.${phase}`, { defaultValue: humanise(phase) }) : t('gate.fallback');

/**
 * Crew role → emoji. Not translated: these are keyed on the engine's English role
 * names and the glyph carries no language.
 */
export const CREW_ICONS: Record<string, string> = {
  'Script Breakdown (1st AD)': '📋',
  'Script Supervisor': '🎬✔️',
  'Casting Director': '🎭',
  'Director of Photography': '📷',
  'Art Department': '🎨',
  'Production Designer': '🏗️',
  'Sound Recordist': '🎙️',
  Composer: '🎵',
  'Foley Artist': '🔊',
  'Camera Operator': '🎬',
  Editor: '✂️',
  'Supervising Editor': '🎞️',
  'Re-Recording Mixer': '🎚️',
  'Executive Producer': '🧭',
  'Post Supervisor': '📦',
};

export const crewIcon = (role?: string): string => (role && CREW_ICONS[role]) || '👤';

/** Footage (real Iconik clips) vs Generative (created picture). */
export const productionTypeMeta = (t: Translate, type?: string) => {
  const key = type === 'footage' ? 'footage' : 'generative';
  return { label: t(`type.${key}`), help: t(`type.${key}.help`) };
};
